import { useState, useCallback } from 'react';
import { useHttp } from './useHttp';
import type {
  LeaderboardTab,
  LeaderboardQueryParams,
  LeaderboardResponse,
} from 'src/types/leaderboard';
import type { PaginationMeta } from 'src/types/raffle';

interface UseLeaderboardReturn<T> {
  entries: T[];
  meta: PaginationMeta | null;
  myRank: T | null;
  isLoading: boolean;
  error: string | null;
  fetchLeaderboard: (params?: LeaderboardQueryParams) => void;
}

export function useLeaderboard<T>(tab: LeaderboardTab): UseLeaderboardReturn<T> {
  const { isLoading, error, sendRequest } = useHttp();
  const [entries, setEntries] = useState<T[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [myRank, setMyRank] = useState<T | null>(null);

  const fetchLeaderboard = useCallback(
    (params?: LeaderboardQueryParams) => {
      const queryParams = new URLSearchParams();
      const p = params ?? {};

      if (p.search) queryParams.set('search', p.search);
      if (p.sortBy) queryParams.set('sortBy', p.sortBy);
      if (p.sortOrder) queryParams.set('sortOrder', p.sortOrder);
      if (p.page) queryParams.set('page', String(p.page));
      if (p.limit) queryParams.set('limit', String(p.limit));
      if (p.userId) queryParams.set('userId', p.userId);
      if (p.period && p.period !== 'all') queryParams.set('period', p.period);

      const qs = queryParams.toString();
      const url = `leaderboard/${tab}${qs ? `?${qs}` : ''}`;

      sendRequest({ url }, (response: unknown) => {
        const res = response as LeaderboardResponse<T>;
        setEntries(res.data);
        setMeta(res.meta);
        setMyRank(res.myRank ?? null);
      });
    },
    [sendRequest, tab],
  );

  return { entries, meta, myRank, isLoading, error, fetchLeaderboard };
}
